import Auth from '@aws-amplify/auth';
import { postRequest, getRequest } from 'utilities/service.utils';
import configureAmplify from 'aws.config.exports';
import createUrlBuilder, {
  SSO_LOGOUT_REDIRECT_URL
} from 'utilities/url.builder.utils';
import AppService from './app.service.context';
import {
  LoginCredentials,
  RegistrationDataTypes,
  UserAccount,
  ConfirmationDataTypes,
  LOCAL_STORAGE_KEYS,
  OTPCredentials,
  AUTH_TYPE
} from 'types';

export const amplifyCurrentUser = async () => Auth.currentAuthenticatedUser();
export const amplifyGetCurrentSession = async () => Auth.currentSession();
export const userSession = async (user: any) => Auth.userSession(user);
export const otpAmplifyConfiguration = () => {
  Auth.configure({
    authenticationFlowType: process.env.REACT_APP_AUTHENTICATION_FLOW_TYPE,
    userPoolWebClientId: process.env.REACT_APP_USER_POOL_WEB_CLIENT_ID
  });
};
export const externalAppAmplifyConfiguration = () => {
  Auth.configure({
    authenticationFlowType: process.env.REACT_APP_AUTHENTICATION_FLOW_TYPE,
    userPoolWebClientId: process.env.REACT_APP_USER_POOL_EXTERNAL_APP_CLIENT_ID
  });
};
export const smartphoneAmplifyConfiguration = () => {
  Auth.configure({
    authenticationFlowType: process.env.REACT_APP_AUTHENTICATION_FLOW_TYPE,
    userPoolWebClientId: process.env.REACT_APP_USER_POOL_SMARTPHONE_CLIENT_ID
  });
};

export const login = async ({ username, password }: LoginCredentials) =>
  await Auth.signIn(username, password);

export const logout = async () => await Auth.signOut();

export const ssoLogout = async () => {
  localStorage.removeItem(LOCAL_STORAGE_KEYS.AWS_SIGNIN_HOSTED_UI);
  localStorage.removeItem(LOCAL_STORAGE_KEYS.AUTH_JWT_TOKEN_ID);
  await Auth.signOut().then(() => {
    window.location.href = SSO_LOGOUT_REDIRECT_URL;
  });
};

export const loginWithSSO = async (redirectTo?: string) => {
  await configureAmplify();
  let customState = undefined;
  if (redirectTo && redirectTo !== '/') {
    customState = redirectTo;
  }
  await Auth.federatedSignIn({ customProvider: 'DBYDSIGNIN', customState });
};

export const signupWithSSOService = async () => {
  await configureAmplify();
  await Auth.federatedSignIn({ customProvider: 'DBYDSIGNUP' });
};

export const forgotSSOPassword = async () =>
  await Auth.federatedSignIn({ customProvider: 'DBYDFORGOT' });

export const userRegistration = async (payload: RegistrationDataTypes) =>
  await Auth.signUp({ ...payload, username: payload.username });

export const userConfirmRegistration = async ({
  username,
  confirmationCode
}: ConfirmationDataTypes) =>
  await Auth.confirmSignUp(username, confirmationCode);

export const loginUser = (credentials: LoginCredentials): any => {
  return login(credentials)
    .then((user: UserAccount) => {
      return {
        user: {
          username: user.username,
          email: user.attributes.email,
          token: user.signInUserSession.idToken.jwtToken
        }
      };
    })
    .catch((error) => {
      return {
        error: {
          message: error.message,
          code: error?.details?.code
        }
      };
    });
};

export const getAuthClientId = (loginType: AUTH_TYPE) => {
  switch (loginType) {
    case AUTH_TYPE.OPT_LOGIN:
      return process.env.REACT_APP_USER_POOL_WEB_CLIENT_ID || '';
    case AUTH_TYPE.EXTERNAL_APP_LOGIN:
      return process.env.REACT_APP_USER_POOL_EXTERNAL_APP_CLIENT_ID || '';
    case AUTH_TYPE.SMARTPHONE_LOGIN:
      return process.env.REACT_APP_USER_POOL_SMARTPHONE_CLIENT_ID || '';
    default:
      return process.env.REACT_APP_COGNITO_APP_CLIENT_ID || '';
  }
};

export const fetchCurrentUser = async (loginType: AUTH_TYPE) => {
  if (loginType === AUTH_TYPE.OPT_LOGIN) otpAmplifyConfiguration();
  if (loginType === AUTH_TYPE.EXTERNAL_APP_LOGIN)
    externalAppAmplifyConfiguration();
  if (loginType === AUTH_TYPE.SMARTPHONE_LOGIN)
    smartphoneAmplifyConfiguration();

  return amplifyCurrentUser().then((user: UserAccount) => {
    if (user) {
      const _user = {
        username: user.username,
        token: user.signInUserSession?.idToken?.jwtToken
      };
      return {
        user: _user
      };
    } else {
      return {
        user: {}
      };
    }
  });
};

export const fetchUserTermsAcceptanceService = async (userTermsId: number) => {
  const urlBuilder = await createUrlBuilder(
    `/system/userterms/${userTermsId}/accept`
  );
  const responseBody = await postRequest(AppService, urlBuilder.toString())
    .then((response: any) => {
      return response;
    })
    .catch((error) => {
      return error.response;
    });
  return responseBody;
};

export const selectUserSessionOrganisationService = async (payload: any) => {
  const urlBuilder = createUrlBuilder(`/community/auth/sessionorganisation`);
  return await postRequest(AppService, urlBuilder?.toString(), payload);
};

export const refreshUserAuthToken = async () => {
  const user = await amplifyCurrentUser();
  const { refreshToken } = user.getSignInUserSession();
  const session = await amplifyGetCurrentSession();
  if (session) {
    const userRefresh = await user.refreshSession(
      refreshToken,
      (error: any, refreshSession: any) => {
        try {
          if (!error) {
            const jwtToken = refreshSession.idToken.jwtToken || null;
            localStorage.setItem(
              LOCAL_STORAGE_KEYS.AUTH_JWT_TOKEN_ID,
              jwtToken
            );
          } else {
            console.error('--gwtToken refresh failed-- ', error);
            localStorage.removeItem(LOCAL_STORAGE_KEYS.AUTH_JWT_TOKEN_ID);
            throw error;
          }
        } catch (error) {
          console.error('--gwtToken refresh failed-- ', error);
        }
      }
    );
    return userRefresh;
  }

  return null;
};

export const fetchOTPUsername = async (email: any) => {
  const URL = `/community/users/anonymous?email=${email}`;
  const urlBuilder = createUrlBuilder(URL);
  const result =
    urlBuilder && (await getRequest(AppService, urlBuilder.toString()));

  return result;
};

export const fetchOTPCode = async (username: any) => {
  otpAmplifyConfiguration();
  return await Auth.signIn(username);
};

export const SignInWithOTPCode = async (otpCredentials: OTPCredentials) => {
  return await Auth.sendCustomChallengeAnswer(
    otpCredentials.cognitoUserSession,
    otpCredentials.code
  );
};

export const SignInWithExternalApp = async (payload: any) => {
  externalAppAmplifyConfiguration();
  const payloadBase64Str = payload.token.split('.')[1];
  const payloadStr = atob(payloadBase64Str);
  const clientMetaData = JSON.parse(payloadStr);
  const { clientId, externalAppId, externalAppName } = clientMetaData;
  const response = await Auth.signIn(payload.username);
  return await Auth.sendCustomChallengeAnswer(response, payload.token, {
    clientId,
    challengeName: response.challengeName,
    externalAppId,
    externalAppName
  });
};

export const SignInWithTemporaryCode = async (payload: {
  username: string;
  code: string;
  codeType: string;
}) => {
  smartphoneAmplifyConfiguration();
  const response = await Auth.signIn(payload.username);
  return await Auth.sendCustomChallengeAnswer(response, payload.code, {
    codeType: payload.codeType,
    clientId: process.env.REACT_APP_USER_POOL_SMARTPHONE_CLIENT_ID || '',
    challengeName: response.challengeName
  });
};

export const clearCognitoLocalStorage = () => {
  var keys: string[] = [];
  for (var a = 0; a < localStorage.length; a++) {
    if (localStorage.key(a)?.startsWith('CognitoIdentityServiceProvider.')) {
      keys.push(localStorage.key(a) || '');
    }
  }

  for (var b = 0; b < keys.length; b++) {
    localStorage.removeItem(keys[b]);
  }
};
